import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Bell, Trash2 } from "lucide-react";

export default function NotificationsPage() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title:"", body:"", type:"restock", product_id:"", target_store_ids:[] });

  const reload = async () => {
    const r = await api.get("/notifications"); setNotes(r.data);
    if (user.role === "admin") {
      const [s, p] = await Promise.all([api.get("/stores"), api.get("/products")]);
      setStores(s.data); setProducts(p.data);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); reload(); };

  const del = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    try { await api.delete(`/notifications/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clearAll = async () => {
    if (!notes.length) return;
    if (!window.confirm(`Clear all ${notes.length} notification${notes.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    try {
      const r = await api.delete("/notifications");
      toast.success(`Cleared ${r.data?.deleted ?? 0} notifications`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notifications", {
        ...form, product_id: form.product_id || null,
        target_store_ids: form.target_store_ids,
      });
      toast.success("Notification sent");
      setOpen(false); setForm({ title:"", body:"", type:"restock", product_id:"", target_store_ids:[] }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleStore = (sid) => {
    setForm(f => f.target_store_ids.includes(sid)
      ? { ...f, target_store_ids: f.target_store_ids.filter(x => x !== sid) }
      : { ...f, target_store_ids: [...f.target_store_ids, sid] });
  };

  return (
    <div className="space-y-8" data-testid="notifications-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div><div className="overline">Communication</div><h1 className="font-display text-4xl sm:text-5xl mt-2">Notifications</h1></div>
        <div className="flex items-center gap-2">
          {notes.length > 0 && (
            <Button variant="outline" onClick={clearAll} data-testid="clear-all-notifications-btn">
              <Trash2 className="w-4 h-4 mr-2"/>Clear all
            </Button>
          )}
          {user.role === "admin" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="send-notification-btn"><Send className="w-4 h-4 mr-2"/>Send notification</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">Compose notification</DialogTitle>
                <DialogDescription className="sr-only">Send a message to a store or the entire network.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Type</Label>
                    <Select value={form.type} onValueChange={v=>setForm({...form, type:v})}>
                      <SelectTrigger data-testid="notif-type"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="restock">Restock alert</SelectItem>
                        <SelectItem value="new_title">New title</SelectItem>
                        <SelectItem value="announcement">Announcement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Related product (optional)</Label>
                    <Select value={form.product_id} onValueChange={v=>setForm({...form, product_id:v})}>
                      <SelectTrigger data-testid="notif-product"><SelectValue placeholder="None"/></SelectTrigger>
                      <SelectContent className="max-h-60">{products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Title</Label><Input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} required data-testid="notif-title"/></div>
                <div><Label>Message</Label><Textarea rows={4} value={form.body} onChange={e=>setForm({...form, body:e.target.value})} required data-testid="notif-body"/></div>
                <div>
                  <Label>Recipients <span className="text-xs text-muted-foreground">(leave empty to send to all stores)</span></Label>
                  <div className="mt-2 max-h-40 overflow-y-auto border border-border rounded-md p-3 space-y-1.5">
                    {stores.map(s => (
                      <div key={s.id} className="flex items-center gap-2">
                        <Checkbox id={`r-${s.id}`} checked={form.target_store_ids.includes(s.id)} onCheckedChange={()=>toggleStore(s.id)} data-testid={`recipient-${s.code}`}/>
                        <label htmlFor={`r-${s.id}`} className="text-sm cursor-pointer">{s.name}</label>
                      </div>
                    ))}
                  </div>
                </div>
                <DialogFooter><Button type="submit" data-testid="notif-submit">Send</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="grid-border-card overflow-hidden">
        <div className="divide-y divide-border">
          {notes.map(n => (
            <div key={n.id} className={`p-6 transition-colors ${!n.read ? "bg-accent/5" : ""}`} data-testid="notification-item">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${!n.read ? "bg-accent/15 text-accent" : "bg-secondary text-muted-foreground"}`}>
                    <Bell className="w-4 h-4" strokeWidth={1.5}/>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{n.title}</h3>
                      <Badge variant="outline" className="text-xs">{n.type}</Badge>
                      {!n.read && <Badge className="text-xs bg-accent text-accent-foreground">New</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{n.body}</p>
                    <div className="text-xs text-muted-foreground mt-2 font-mono">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                </div>
                {!n.read && <Button variant="ghost" size="sm" onClick={()=>markRead(n.id)} data-testid={`mark-read-${n.id}`}>Mark read</Button>}
                <Button variant="ghost" size="icon" onClick={()=>del(n.id)} data-testid={`delete-notification-${n.id}`} aria-label="Delete notification">
                  <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive"/>
                </Button>
              </div>
            </div>
          ))}
          {notes.length === 0 && <div className="text-center py-20 text-muted-foreground">No notifications</div>}
        </div>
      </div>
    </div>
  );
}
